import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useBootstrapStatus, useLogin } from '../lib/queries.js';
import { ApiError } from '../lib/api.js';
import { GoogleSignInButton } from './GoogleSignInButton.js';

/* ------------------------------------------------------------------ */
/* Fundo: constelação de nós + arestas (SVG puro, sem imagens)         */
/* ------------------------------------------------------------------ */

// [x, y, raio] — viewBox 800×600
const GRAPH_NODES: ReadonlyArray<readonly [number, number, number]> = [
  [80, 120, 3], [190, 70, 2.2], [300, 150, 3.5], [140, 260, 2.5],
  [320, 300, 4], [470, 90, 2.5], [520, 230, 3], [640, 160, 2.2],
  [700, 320, 3.5], [560, 400, 2.5], [400, 470, 3], [220, 440, 2.2],
  [90, 380, 2.8], [740, 60, 2], [680, 480, 2.4], [300, 560, 2],
  [520, 540, 2.6], [760, 200, 2.4],
];

// pares de índices de GRAPH_NODES
const GRAPH_EDGES: ReadonlyArray<readonly [number, number]> = [
  [0, 1], [1, 2], [2, 4], [0, 3], [3, 12], [3, 4], [4, 6], [2, 5],
  [5, 6], [5, 13], [6, 7], [7, 17], [7, 8], [8, 9], [6, 8], [9, 10],
  [10, 4], [10, 11], [11, 12], [9, 16], [16, 14], [8, 14], [15, 11],
  [15, 10], [13, 17],
];

// Keyframes que o Tailwind não cobre; tudo gated em prefers-reduced-motion.
const GRAPH_CSS = `
@media (prefers-reduced-motion: no-preference) {
  .fm-node { animation: fm-pulse 7s ease-in-out infinite; }
  .fm-drift { animation: fm-drift 80s ease-in-out infinite alternate; }
  .fm-signal { stroke-dasharray: 5 130; animation: fm-signal 10s linear infinite; }
}
@keyframes fm-pulse { 0%, 100% { opacity: 0.25; } 50% { opacity: 0.9; } }
@keyframes fm-drift {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-18px, 12px, 0); }
}
@keyframes fm-signal { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -270; } }
`;

function GraphBackdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      {/* brilho cyan difuso atrás da área da marca */}
      <div className="absolute -left-40 top-1/2 h-[36rem] w-[36rem] -translate-y-1/2 rounded-full bg-cyan-500/10 blur-[120px]"></div>
      <svg
        className="absolute inset-0 h-full w-full text-cyan-400 opacity-70 [mask-image:radial-gradient(ellipse_at_center,black,transparent_78%)] lg:[mask-image:linear-gradient(to_right,black_35%,transparent_92%)]"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
      >
        <g className="fm-drift">
          {GRAPH_EDGES.map(([a, b], i) => {
            const na = GRAPH_NODES[a];
            const nb = GRAPH_NODES[b];
            if (!na || !nb) return null;
            const signal = i % 5 === 0;
            return (
              <line
                key={i}
                x1={na[0]} y1={na[1]}
                x2={nb[0]} y2={nb[1]}
                stroke="currentColor"
                strokeWidth={signal ? 1 : 0.6}
                opacity={signal ? 0.5 : 0.16}
                className={signal ? 'fm-signal' : undefined}
              />
            );
          })}
          {GRAPH_NODES.map(([x, y, r], i) => (
            <circle
              key={i}
              cx={x} cy={y} r={r}
              fill="currentColor"
              className="fm-node"
              style={{ opacity: 0.5, animationDelay: `${(i * 0.9) % 7}s` }}
            />
          ))}
        </g>
      </svg>
    </div>
  );
}

function LogoGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.5" opacity="0.45" fill="none">
        <line x1="8" y1="22" x2="16" y2="9"></line>
        <line x1="16" y1="9" x2="25" y2="18" />
        <line x1="8" y1="22" x2="25" y2="18" />
        <line x1="25" y1="18" x2="22" y2="27" />
      </g>
      <circle cx="16" cy="9" r="3.5" fill="currentColor" />
      <circle cx="8" cy="22" r="3" fill="currentColor" />
      <circle cx="25" cy="18" r="2.5" fill="currentColor" />
      <circle cx="22" cy="27" r="1.8" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Página de login                                                     */
/* ------------------------------------------------------------------ */

const INPUT_CLASS =
  'w-full rounded-lg border border-zinc-800 bg-zinc-900/80 px-3.5 py-2.5 text-zinc-100 ' +
  'placeholder:text-zinc-600 transition-colors focus:outline-none focus:border-cyan-500 ' +
  'focus:ring-2 focus:ring-cyan-500/20 aria-invalid:border-rose-500/60';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const login = useLogin();
  const bootstrap = useBootstrapStatus();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login.mutateAsync({ email, password });
      // Reload completo pra garantir cookie + cache limpo (evita race com beforeLoad de TanStack Router).
      window.location.href = '/';
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) setError('Muitas tentativas. Tente novamente em alguns minutos.');
        else if (err.status === 401) setError('E-mail ou senha incorretos.');
        else if (err.status === 403) setError('Conta banida.');
        else setError(err.message);
      } else {
        setError('Erro inesperado');
      }
    }
  }

  return (
    <main className="relative grid min-h-dvh overflow-hidden bg-zinc-950 text-zinc-100 lg:grid-cols-[1.1fr_minmax(0,1fr)]">
      <style>{GRAPH_CSS}</style>
      <GraphBackdrop />

      {/* Painel da marca (desktop) */}
      <section className="relative z-10 hidden flex-col justify-center px-16 lg:flex xl:px-24">
        <div className="max-w-md">
          <div className="mb-6">
            <LogoGlyph className="h-10 w-10 text-cyan-400" />
          </div>
          <p className="mb-4 bg-gradient-to-r from-cyan-300 via-cyan-400 to-cyan-600 bg-clip-text text-5xl font-semibold tracking-tight text-transparent">
            Flowmindy
          </p>
          <p className="text-lg text-zinc-400" style={{ textWrap: 'pretty' }}>
            Onde as ideias do seu time se conectam — documentos, grafos e
            conhecimento em um só lugar.
          </p>
        </div>
      </section>

      {/* Formulário */}
      <section className="relative z-10 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Marca compacta (mobile) */}
          <div className="mb-8 flex items-center justify-center gap-2.5 lg:hidden">
            <LogoGlyph className="h-8 w-8 text-cyan-400" />
            <span className="bg-gradient-to-r from-cyan-300 to-cyan-500 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
              Flowmindy
            </span>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-900/60 p-7 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)] backdrop-blur-xl sm:p-8">
            {/* fio de luz no topo do card */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent"
            ></div>

            <h1 className="text-xl font-semibold text-zinc-100">Bem-vindo de volta</h1>
            <p className="mb-6 mt-1 text-sm text-zinc-500">
              Entre para continuar no seu espaço.
            </p>

            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-zinc-300">
                  E-mail
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="voce@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                  className={INPUT_CLASS}
                />
              </div>

              <div>
                <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-zinc-300">
                  Senha
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? 'login-error' : undefined}
                    className={`${INPUT_CLASS} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-zinc-500 transition-colors hover:text-zinc-300 focus-visible:text-cyan-400 focus-visible:outline-2 focus-visible:outline-cyan-500"
                  >
                    {showPassword
                      ? <EyeOff className="h-5 w-5" aria-hidden="true" />
                      : <Eye className="h-5 w-5" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              {/* Slot de erro com altura reservada — sem layout shift */}
              <div className="min-h-6">
                {error && (
                  <p
                    id="login-error"
                    role="alert"
                    className="flex items-start gap-2 text-sm text-rose-400"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={login.isPending}
                className="w-full rounded-lg bg-gradient-to-r from-cyan-500 to-cyan-400 py-2.5 font-semibold text-zinc-950 transition-all hover:from-cyan-400 hover:to-cyan-300 hover:shadow-[0_0_24px_-6px_rgba(34,211,238,0.6)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
              >
                <span className="flex items-center justify-center gap-2">
                  {login.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                      Entrando…
                    </>
                  ) : (
                    'Entrar'
                  )}
                </span>
              </button>
            </form>

            <GoogleSignInButton />
          </div>

          <div className="mt-6 text-center text-sm text-zinc-500">
            {bootstrap.data?.needs_first_user ? (
              <Link
                to="/register"
                className="text-cyan-400 underline-offset-4 transition-colors hover:text-cyan-300 hover:underline"
              >
                Criar conta superadmin (primeira do sistema)
              </Link>
            ) : (
              <>
                Não tem conta?{' '}
                <Link
                  to="/register"
                  className="text-cyan-400 underline-offset-4 transition-colors hover:text-cyan-300 hover:underline"
                >
                  Criar conta
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
